import {
    concatBytes,
    readId,
    readUInt,
    readVInt,
    writeElement,
    writeFloat64,
    writeId,
    writeVInt,
} from './ebml';

const ID_EBML_HEADER = 0x1a45dfa3;
const ID_SEGMENT = 0x18538067;
const ID_INFO = 0x1549a966;
const ID_TIMECODE_SCALE = 0x2ad7b1;
const ID_DURATION = 0x4489;

/** Matroska's default: timecodes are expressed in milliseconds. */
const DEFAULT_TIMECODE_SCALE_NS = 1000000;
const NS_PER_MS = 1000000;

interface Element {
    id: number;
    /** Offset of the element's first ID byte. */
    start: number;
    /** Offset of the element's first content byte. */
    contentStart: number;
    /** Offset one past the last content byte, or `null` when the size is unknown. */
    contentEnd: number | null;
}

const readElement = (bytes: Uint8Array, offset: number): Element | null => {
    const id = readId(bytes, offset);
    if (!id) return null;

    const size = readVInt(bytes, offset + id.length);
    if (!size) return null;

    const contentStart = offset + id.length + size.length;
    if (size.unknown) return { id: id.value, start: offset, contentStart, contentEnd: null };

    const contentEnd = contentStart + size.value;
    if (contentEnd > bytes.length) return null;

    return { id: id.value, start: offset, contentStart, contentEnd };
};

const findChild = (bytes: Uint8Array, start: number, end: number, id: number): Element | null => {
    let offset = start;

    while (offset < end) {
        const element = readElement(bytes, offset);
        // An unknown-size element runs to the end of its parent, so the walk cannot step over it.
        if (!element || element.contentEnd === null) return null;
        if (element.id === id) return element;
        offset = element.contentEnd;
    }

    return null;
};

const readTimecodeScale = (bytes: Uint8Array, start: number, end: number): number => {
    const scale = findChild(bytes, start, end, ID_TIMECODE_SCALE);
    if (!scale || scale.contentEnd === null) return DEFAULT_TIMECODE_SCALE_NS;

    const value = readUInt(bytes, scale.contentStart, scale.contentEnd);
    return value > 0 ? value : DEFAULT_TIMECODE_SCALE_NS;
};

/** Rebuilds Info with a single, correct Duration child. */
const withDuration = (bytes: Uint8Array, info: Element, durationMs: number): Uint8Array | null => {
    const contentEnd = info.contentEnd;
    if (contentEnd === null) return null;

    const scaleNs = readTimecodeScale(bytes, info.contentStart, contentEnd);
    const duration = writeElement(ID_DURATION, writeFloat64((durationMs * NS_PER_MS) / scaleNs));

    const children: Uint8Array[] = [];
    let offset = info.contentStart;

    while (offset < contentEnd) {
        const child = readElement(bytes, offset);
        if (!child || child.contentEnd === null) return null;
        // Drop any Duration already present — ours replaces it.
        if (child.id !== ID_DURATION) children.push(bytes.subarray(child.start, child.contentEnd));
        offset = child.contentEnd;
    }

    if (offset !== contentEnd) return null;

    return writeElement(ID_INFO, concatBytes([...children, duration]));
};

const patchDuration = (bytes: Uint8Array, durationMs: number): Uint8Array | null => {
    const header = readElement(bytes, 0);
    if (!header || header.id !== ID_EBML_HEADER || header.contentEnd === null) return null;

    const segment = readElement(bytes, header.contentEnd);
    if (!segment || segment.id !== ID_SEGMENT) return null;

    // MediaRecorder leaves Segment open-ended; everything after its header is its content.
    const segmentEnd = segment.contentEnd ?? bytes.length;

    // Info always precedes the first Cluster, so this walk never has to step over a
    // live-mode Cluster (which Chrome writes with an unknown size).
    const info = findChild(bytes, segment.contentStart, segmentEnd, ID_INFO);
    if (!info || info.contentEnd === null) return null;

    const patchedInfo = withDuration(bytes, info, durationMs);
    if (!patchedInfo) return null;

    const segmentPayload = concatBytes([
        bytes.subarray(segment.contentStart, info.start),
        patchedInfo,
        bytes.subarray(info.contentEnd, segmentEnd),
    ]);

    return concatBytes([
        bytes.subarray(0, header.contentEnd),
        writeId(ID_SEGMENT),
        // Replacing the unknown size with a real one also tells players where the segment ends.
        writeVInt(segmentPayload.length),
        segmentPayload,
    ]);
};

/**
 * MediaRecorder writes WebM in "live" mode: the Segment size is left unknown and Info
 * carries no Duration, because neither is known while recording. Players then report a
 * duration of `Infinity`, draw no usable seek bar, and cannot map a seek position onto
 * the timeline — which is what makes these uploads look broken in a web player.
 *
 * This rewrites both fields once the segment is closed and its real length is known.
 * Anything that cannot be confidently parsed is returned untouched, so a non-WebM
 * recording (Safari emits MP4) simply passes through.
 */
export const withWebmDuration = async (blob: Blob, durationMs: number): Promise<Blob> => {
    if (!blob.size || durationMs <= 0 || !blob.type.startsWith('video/webm')) return blob;

    try {
        const patched = patchDuration(new Uint8Array(await blob.arrayBuffer()), durationMs);
        return patched ? new Blob([patched], { type: blob.type }) : blob;
    } catch {
        return blob;
    }
};
